import React, { useContext, useMemo, useState } from 'react';
import { clone, uniqWith } from 'ramda';
import { ITreeSection } from '../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import { sortBokningsrubriker } from '../../../Rutiner/Kalender/Components/SchedulerComponent/Utils/resourcesSortUtils';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { EDAutoCompleteEdit } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDChip from '../../../../../../BaseComponents/Chip/EDChip';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDTreeSelect from '../../../../../../BaseComponents/TreeSelect/EDTreeSelect';
import { IBokningsrubrik } from '../../../ObjektVyer/Aktivitet/AktivitetResursValjare';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { useQuery } from '@apollo/client';
import meQuery from '../../../../Bokning/Rutiner/Kalender/Queries/MeQuery/MeQuery.gql';
import {
  ResursQuery,
  ResursQuery_db_BOResurs_All,
  ResursQueryVariables,
} from '../../../../Bokning/Rutiner/Kalender/Queries/ResursQuery/_QueryTypes/ResursQuery';
import resursQuery from '../../../../Bokning/Rutiner/Kalender/Queries/ResursQuery/ResursQuery.gql';
import mergeSort from '../../../../../../Utils/mergeSort';
import { bokningsrubrikFromDbResurs } from '../../../ObjektVyer/Aktivitet/Utils/AktivitetUtils';
import {
  IResursTyp,
  ResursTypContext,
} from '../../../Contexts/ResursTypContext/ResursTypContext';
import {
  MeQuery,
  MeQueryVariables,
} from '../../Kalender/Queries/MeQuery/_QueryTypes/MeQuery';
import { ResursBastyp } from './PlaneringToolbar';

interface IResursValjareProps {
  allowedBaseTypes?: ResursBastyp[];
  bokningsrubriker: IBokningsrubrik[];
  setBokningsrubriker: (value: IBokningsrubrik[]) => void;
}

export const ResursValjare: React.FC<IResursValjareProps> = ({
  allowedBaseTypes,
  bokningsrubriker,
  setBokningsrubriker,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const resursTypContext = useContext(ResursTypContext);
  const resTypes = resursTypContext.resTypes;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [sections, setSections] = useState<ITreeSection<IBokningsrubrik>[]>([]);

  const activeDatabaseOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabase ?? 'noDBYet',
      },
      skip: !activeDatabase,
    }),
    [activeDatabase]
  );

  // Ifall man vill återanvända denna komponent i andra rutiner så får man flytta ut hämtning av resurser. Och även ändra rutinID som är hårdkodat till 16001.
  const resursQueryOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabase ?? '',
        routineID: 16001,
      },
      skip: !activeDatabase,
    }),
    [activeDatabase]
  );

  // Ifall man vill återanvända denna komponent i andra rutiner så får man flytta ut hämtning av resurser. Och även ändra rutinID som är hårdkodat till 16001.
  const { data: resurs_data } = useQuery<ResursQuery, ResursQueryVariables>(
    resursQuery,
    resursQueryOptions
  );

  const { data: me_data } = useQuery<MeQuery, MeQueryVariables>(
    meQuery,
    activeDatabaseOptions
  );

  const resources = useMemo((): IBokningsrubrik[] => {
    if (!resurs_data) return [];

    return mergeSort(
      resurs_data.db?.BOResurs_All ?? [],
      (a, b) => (a.PRIO_ORDNING ?? 1000) - (b.PRIO_ORDNING ?? 1000)
    )
      .filter(resource => {
        if (!allowedBaseTypes || allowedBaseTypes.length === 0) return true;
        return allowedBaseTypes.includes(
          resource._Associations?.ResRestyp?.Bastyp as ResursBastyp
        );
      })
      .map(resource => {
        const resRestypNamn =
          resource?._Associations?.ResRestyp?.BO_RESURSTYP_NAMN || '';
        return {
          resursText: `${resource.BO_RESURS_NAMN} (${resRestypNamn})`,
          resursNamn: resource.BO_RESURS_NAMN,
          BO_RESURS_ID: resource.BO_RESURS_ID,
          resursId: resource.id,
          resursTyp: resRestypNamn,
          platsId: resource._Associations?.Forsamling?.id || '',
        };
      });
  }, [resurs_data, allowedBaseTypes]);

  const getUniqResourceBaseTypes = (
    resources: ResursQuery_db_BOResurs_All[]
  ) => {
    let resourcesBaseTypes = resources?.map(item => ({
      name: item._Associations?.ResRestyp?.Bastyp,
      value: item.id,
    }));

    const importOrder = ['Plats', 'Person', 'Övrigt'];

    const sortByObject = importOrder.reduce<Record<string, number>>(
      (obj, item, index) => {
        return {
          ...obj,
          [item]: index,
        };
      },
      {}
    );

    const customSort = mergeSort(
      resourcesBaseTypes,
      (a, b) => sortByObject[a.name as string] - sortByObject[b.name as string]
    );

    const uniq = uniqWith((a, b) => a.name === b.name, customSort);

    return uniq;
  };

  const GetSubList = (
    resourceData: ResursQuery,
    resTypes: IResursTyp[],
    checkedResources: string[],
    data: string,
    userSign: string | undefined
  ): ITreeSection[] => {
    let subList: ITreeSection[] = [];

    resTypes?.forEach(resType => {
      if (resType.foraldratyp === data) {
        const resSubTypes =
          GetSubList(
            resourceData,
            resTypes,
            checkedResources,
            resType.namn,
            userSign
          ) ?? [];
        const resources =
          resourceData?.db?.BOResurs_All?.reduce<ITreeSection[]>(
            (acc, item2) => {
              if (
                item2._Associations?.ResRestyp?.BO_RESURSTYP_NAMN ===
                  resType.namn &&
                item2.Resursnamn &&
                !checkedResources.includes(item2.id)
              ) {
                acc.push({
                  name: item2.Resursnamn,
                  value: item2.BO_RESURS_ID,
                  id: item2.BO_RESURS_ID,
                  data: bokningsrubrikFromDbResurs(item2),
                });
              }
              return acc;
            },
            []
          ) ?? [];
        if (resSubTypes.length > 0 || resources.length > 0) {
          subList.push({
            name: resType.namn,
            value: resType.id,
            subList: [...resSubTypes, ...resources],
            id: resType.id,
          });
        }
      }
    });

    return subList;
  };

  const getSections = useMemo(() => {
    if (resurs_data) {
      const checkedResources = bokningsrubriker.map(item => item.resursId);
      let resourcesBaseItems = [];
      if (resurs_data?.db?.BOResurs_All) {
        let baseResources = getUniqResourceBaseTypes(
          resurs_data?.db?.BOResurs_All
        );

        if (allowedBaseTypes && allowedBaseTypes.length > 0) {
          baseResources = baseResources.filter(item =>
            allowedBaseTypes.includes(item.name as ResursBastyp)
          );
        }

        resourcesBaseItems = baseResources.map((item: any) => {
          return {
            name: item.name,
            value: item.value,
            id: item.value,
            subList: GetSubList(
              resurs_data,
              resTypes(),
              checkedResources,
              item.name,
              me_data?.me?.[0]?.sign
            ),
            open: true,
          };
        });
      } else {
        resourcesBaseItems = [
          { name: 'Inga resurser hittades', value: '', id: '' },
        ];
      }

      return resourcesBaseItems;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resurs_data, resTypes, bokningsrubriker, me_data?.me]);

  const openResursDialog = () => setDialogOpen(true);
  const closeResursDialog = () => setDialogOpen(false);

  const removeBokningsrubrikChip = (resursId: string) =>
    bokningsrubriker.filter(item => item.resursId !== resursId);

  const getBokningsrubrikerFromSections = (
    sections: ITreeSection<IBokningsrubrik>[]
  ): IBokningsrubrik[] => {
    const result: IBokningsrubrik[] = [];
    const extractCheckedItems = (items: any[]) => {
      items.forEach(item => {
        if (item.subList) {
          extractCheckedItems(item.subList);
        } else if (item.checked && item.data) {
          result.push(item.data);
        }
      });
    };
    extractCheckedItems(sections);
    return result;
  };

  const getHiddenCheckedResources = (
    ResursTyp: ITreeSection<any>[]
  ): ITreeSection<any>[] => {
    for (let i = 0; i < ResursTyp?.length; i++) {
      // eslint-disable-next-line no-prototype-builtins
      if (ResursTyp[i].hasOwnProperty('checked')) {
        if (ResursTyp?.[i].checked) {
          ResursTyp[i].hidden = ResursTyp[i].checked;
        }
      } else if (ResursTyp[i].subList) {
        getHiddenCheckedResources(ResursTyp[i].subList!);
      }
    }
    return ResursTyp;
  };

  const getUncheckCurrentUrval = (
    ResursTyp: ITreeSection<any>[]
  ): ITreeSection<any>[] => {
    for (let i = 0; i < ResursTyp?.length; i++) {
      // eslint-disable-next-line no-prototype-builtins
      if (ResursTyp[i].hasOwnProperty('checked')) {
        if (ResursTyp?.[i].checked && !ResursTyp?.[i].hidden)
          ResursTyp[i].checked = false;
      } else if (ResursTyp[i].subList) {
        getUncheckCurrentUrval(ResursTyp[i].subList!);
      }
    }
    return ResursTyp;
  };

  const unCheck = () => {
    setSections(getUncheckCurrentUrval(clone(sections)));
  };

  const selectResursDialog = () => {
    const newBokningsRubriker = getBokningsrubrikerFromSections(sections);
    const existingResourcesMap = bokningsrubriker.reduce<
      Record<string, IBokningsrubrik>
    >((map, item) => {
      map[item.resursId] = item;
      return map;
    }, {});
    const mergedBokningsRubriker = [...bokningsrubriker];
    newBokningsRubriker.forEach(newItem => {
      if (!existingResourcesMap[newItem.resursId]) {
        mergedBokningsRubriker.push(newItem);
      }
    });
    setBokningsrubriker(mergedBokningsRubriker);
    closeResursDialog();
  };

  return (
    <EDBox display="flex" flexDirection="column" alignItems="start">
      <EDTypography color="grey" bold marginRight={'auto'}>
        Resurser
      </EDTypography>
      <EDBox display="flex" flexDirection="row" alignItems="center">
        <EDBox>
          <EDIcon
            iconType="resources_filter"
            color="inherit"
            size="medium"
            marginRight={2}
            marginTop={'5px'}
          />
        </EDBox>
        <EDBox width={'350px'}>
          <EDAutoCompleteEdit<IBokningsrubrik, true>
            value={bokningsrubriker}
            multiple
            options={resources}
            onChange={values => {
              setBokningsrubriker(values);
            }}
            label="Välj minst en resurs"
            fullWidth
            getOptionLabel={option => option.resursText}
            getOptionSelected={(option, value) =>
              option.resursId === value.resursId
            }
            renderTags={values => {
              return sortBokningsrubriker(values).map(value => (
                <EDChip
                  margin={0.5}
                  onDelete={() => {
                    setBokningsrubriker(
                      removeBokningsrubrikChip(value.resursId)
                    );
                  }}
                  key={value.BO_RESURS_ID || ''}
                  label={
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <EDBox
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          lineHeight: '1em',
                          margin: '0px',
                        }}
                      >
                        <div>{value.resursNamn}</div>
                      </EDBox>
                    </div>
                  }
                />
              ));
            }}
            hideClearIcon
            hideClear
            testId="calendar_report_resources"
          />
        </EDBox>

        <EDBox sx={{ marginTop: '7px' }}>
          <EDButton
            padding={1}
            onClick={openResursDialog}
            label={isSmallScreen ? '' : `Lägg Till`}
            resourceBtnStyling={true}
            icon={<EDIcon iconType="add" color="inherit" />}
            flat
            size="small"
            data-inline="true"
          >
            <EDDialog
              fullWidth
              open={dialogOpen}
              onClose={closeResursDialog}
              footerActions={[
                {
                  label: 'Avmarkera',
                  onClick: unCheck,
                  buttonProps: {
                    disabled:
                      getBokningsrubrikerFromSections(sections).length === 0,
                    disabledReason: 'Inga resurser valda',
                  },
                },
                { label: 'Avbryt', onClick: closeResursDialog },
                { label: 'Använd', onClick: selectResursDialog },
              ]}
              title="Välj Resurs"
              onEnter={() => {
                const updatedSections = clone(getSections);
                const checkedResources = bokningsrubriker.map(
                  item => item.resursId
                );
                const updateCheckedStatus = (sections: any[]) => {
                  sections.forEach(section => {
                    if (section.subList) {
                      updateCheckedStatus(section.subList);
                    } else if (
                      section.data &&
                      checkedResources.includes(section.data.resursId)
                    ) {
                      section.checked = true;
                    }
                  });
                };
                updateCheckedStatus(updatedSections);
                const sectionsWithHiddenChecked =
                  getHiddenCheckedResources(updatedSections);
                setSections(sectionsWithHiddenChecked);
              }}
            >
              <EDTreeSelect
                sections={sections}
                searchValue={''}
                sectionsUpdate={setSections}
              />
            </EDDialog>
          </EDButton>
        </EDBox>
      </EDBox>
    </EDBox>
  );
};
